import Button from "@mui/material/Button";
import {createTheme, styled} from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import {DARK_THEME, ThemePreferences} from "../../contexts/ThemePreferenceContext/ThemePreferenceProvider";
import {MuiTypography, neutralContainedButtonTextColor} from "../../styles/MaterialUIStyling";
import {
  artistreeBrandColors,
  artistreeDarkColors,
  artistreeDarkConstants,
  artistreeHeadingFontSizes,
  artistreeLightColors,
  injectArtistreeCssVariables,
} from "./artistreeTokens";

export const clrPrimaryGreen = artistreeBrandColors.primaryGreen;
export const clrSecondaryGreen = artistreeBrandColors.secondaryGreen;
export const clrRed = artistreeBrandColors.red;
export const clrPurple = artistreeBrandColors.purple;
export const clrYellow = artistreeBrandColors.yellow;
export const clrOrange = artistreeBrandColors.orange;
export const clrBlack = artistreeBrandColors.black;
export const clrPrimaryBlue = artistreeBrandColors.primaryBlue;
export const clrSecondaryBlue = artistreeBrandColors.secondaryBlue;
export const clrPrimaryBlueLight = artistreeBrandColors.primaryBlueLight;
export const clrSecondaryBlueLight = artistreeBrandColors.secondaryBlueLight;
export const clrGrey = artistreeBrandColors.grey;
export const clrWhite = artistreeBrandColors.white;
export const offBrandDarkGreenClr = artistreeBrandColors.offBrandDarkGreen;

// Dark theme colors
export const clrBlackSecondary = artistreeDarkConstants.blackSecondary;
export const clrBlueGrey = artistreeDarkConstants.blueGrey;
export const clrIceBlue = artistreeDarkConstants.iceBlue;
export const clrGreyBorder = artistreeDarkConstants.greyBorder;

export const ourColorsList = ['#FFCB32', '#F7A430', '#39B54A', '#02B3B1', '#175EE9', '#C46CF5'];

export function getOurColorsList(opacity: number) {
  return [
    `hsl(335, 82%, 54%, ${opacity})`,
    `hsl(35, 93%, 58%, ${opacity})`,
    `hsl(128, 52%, 47%, ${opacity})`,
    `hsl(220, 83%, 50%, ${opacity})`,
    `hsl(198, 74%, 51%, ${opacity})`,
    `hsl(279, 87%, 69%, ${opacity})`
  ]
}

declare module "@mui/material/styles" {
  interface Palette {
    negative: Palette["primary"];
    offBrandGreen: Palette["primary"];
    blue: Palette["primary"];
    green: Palette["primary"];
    neutral: Palette["primary"];
  }

  interface PaletteOptions {
    negative?: PaletteOptions["primary"];
    offBrandGreen?: PaletteOptions["primary"];
    blue?: PaletteOptions["primary"];
    green?: PaletteOptions["primary"];
    neutral?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    negative: true;
    offBrandGreen: true;
    blue: true;
    green: true;
    neutral: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    negative: true;
  }
}

const body1 = 15.4;
const horizontalPaddingConstant = "0.5rem";

const hideNumberArrows = {
  '& input[type=number]': {
    MozAppearance: 'textfield'
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  }
}

const outlinedButtonBorder = "2.5px solid";
const offBrandDarkGreenHoverClr = "hsl(181, 35%, 24%)";

export const getArtistreeMaterialUIStyling = (themePreference: ThemePreferences, isDarkTheme: boolean) => {
  injectArtistreeCssVariables();

  const colors = isDarkTheme ? artistreeDarkColors : artistreeLightColors;
  // the outlined brand-bordered buttons keep their label on the theme's muted text colour
  const outlinedLabelColor = isDarkTheme ? clrIceBlue : clrGrey;
  // what MUI gives an outlined color="inherit" button on hover: alpha(text.primary, hoverOpacity)
  const outlinedHoverBackground = isDarkTheme ? "rgba(201, 209, 217, 0.04)" : "rgba(35, 31, 32, 0.04)";

  return createTheme({
    palette: {
      mode: themePreference,
      background: {
        default: colors.backgroundDefault,
        paper: colors.surface
      },
      divider: colors.border,
      common: {
        black: clrBlack
      },
      primary: {
        main: clrPrimaryGreen,
        contrastText: clrWhite,
        dark: "hsl(128, 52%, 44%)",
        light: "hsl(128, 52%, 80%)"
      },
      secondary: {
        main: clrSecondaryGreen,
        contrastText: clrWhite,
        dark: "hsl(86, 53%, 48%)"
      },
      error: {
        main: clrRed
      },
      warning: {
        main: clrYellow,
        contrastText: clrBlack,
        dark: "hsl(45, 100%, 57%)"
      },
      info: {
        main: clrPrimaryBlue,
        contrastText: clrWhite,
        dark: "hsl(198, 74%, 48%)"
      },
      success: {
        main: clrPrimaryGreen,
        contrastText: clrWhite,
        dark: "hsl(128, 52%, 44%)"
      },
      // createPalette does not augment custom colours, so every shade MUI's Button reads is spelled out
      negative: {
        main: clrRed,
        light: "hsl(335, 82%, 70%)",
        dark: "hsl(335, 82%, 49%)",
        contrastText: clrWhite
      },
      offBrandGreen: {
        main: offBrandDarkGreenClr,
        light: "hsl(181, 35%, 40%)",
        dark: offBrandDarkGreenHoverClr,
        contrastText: clrWhite
      },
      blue: {
        main: clrSecondaryBlue,
        light: "hsl(220, 83%, 70%)",
        dark: "hsl(220, 83%, 47%)",
        contrastText: clrWhite
      },
      green: {
        main: clrPrimaryGreen,
        light: "hsl(128, 52%, 80%)",
        dark: "hsl(128, 52%, 44%)",
        contrastText: clrWhite
      },
      neutral: {
        main: clrGrey,
        light: "hsl(0, 0%, 60%)",
        dark: "hsl(0, 0%, 38%)",
        contrastText: clrWhite
      },
      text: {
        primary: colors.text,
        secondary: colors.textSecondary
      },
    },
    shape: {
      borderRadius: 10
    },
    components: {
      MuiAppBar: {
          styleOverrides: {
          colorPrimary: {
            backgroundColor: colors.surfaceSecondary
          }

          },
        },
      MuiPaper: {
          styleOverrides: {
          rounded: {
            border: "1px solid " + colors.border,
            backgroundColor: colors.surface
          }

          },
        },
      MuiButton: {
          // v4 defaulted to color="default"; v5 defaults to "primary" and removed "default", which
          // turns every Button without an explicit colour into a primary-coloured one. "inherit" is
          // v5's stand-in for "default" — see neutralContainedButtonTextColor for the part it misses.
          defaultProps: {
            color: "inherit" as const,
          },
          styleOverrides: {
          root: {
            fontSize: 13.4,
          },
          containedInherit: {
            color: neutralContainedButtonTextColor,
          },
          textSizeLarge: {
            fontSize: "13.4px",
            letterSpacing: "1.25px"
          },
          textSizeSmall: {
            fontSize: "13.4px",
            letterSpacing: "1.25px"
          },
          containedSizeSmall: {
            fontSize: 15
          },
          outlinedSecondary: {
            border: outlinedButtonBorder,
            '&:hover': {
              border: outlinedButtonBorder
            }
          },
          outlinedPrimary: {
            border: outlinedButtonBorder,
            '&:hover': {
              border: outlinedButtonBorder
            }
          },
          outlined: {
            border: outlinedButtonBorder,
            '&:hover': {
              border: outlinedButtonBorder
            },
            color: outlinedLabelColor
          },
          text: {
            color: outlinedLabelColor
          }

          },
          variants: [
            {
              props: {variant: "outlined", color: "negative"},
              style: {
                color: outlinedLabelColor,
                borderColor: clrRed,
                "&:hover": {
                  backgroundColor: isDarkTheme ? "hsla(335, 82%, 54%, 0.1)" : "hsla(335, 82%, 54%, 0.05)",
                  borderColor: clrRed,
                },
              },
            },
            // Decline / Cancel triggers: neutral at rest, red text and tint on hover, no border
            {
              props: {variant: "text", color: "negative"},
              style: {
                color: outlinedLabelColor,
                "&:hover": {
                  backgroundColor: isDarkTheme ? "hsla(335, 82%, 54%, 0.1)" : "hsla(335, 82%, 54%, 0.05)",
                  color: clrRed,
                },
              },
            },
            {
              props: {variant: "outlined", color: "green"},
              style: {
                color: outlinedLabelColor,
                borderColor: clrPrimaryGreen,
                "&:hover": {
                  backgroundColor: outlinedHoverBackground,
                  borderColor: clrPrimaryGreen,
                },
              },
            },
            {
              props: {variant: "outlined", color: "blue"},
              style: {
                color: outlinedLabelColor,
                borderColor: clrSecondaryBlue,
                "&:hover": {
                  backgroundColor: outlinedHoverBackground,
                  borderColor: clrSecondaryBlue,
                },
              },
            },
            {
              // Blue accent ghost button: the icon carries the accent at rest, the label joins it on
              // hover/focus. Dark theme uses the palette's lighter blue for label contrast.
              props: {variant: "text", color: "blue"},
              style: {
                color: outlinedLabelColor,
                "& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiTouchRipple-root": {
                  color: isDarkTheme ? "hsl(220, 83%, 70%)" : clrSecondaryBlue,
                },
                "&:hover, &.Mui-focusVisible": {
                  color: isDarkTheme ? "hsl(220, 83%, 70%)" : clrSecondaryBlue,
                  backgroundColor: isDarkTheme ? "hsla(220, 83%, 50%, 0.12)" : "hsla(220, 83%, 50%, 0.06)",
                },
              },
            },
            {
              // Green accent ghost button, mirroring the blue one above: the icon carries the accent
              // at rest and the label joins it on hover/focus. The green is too light to hold a label
              // against the light theme's white on its own.
              props: {variant: "text", color: "green"},
              style: {
                color: outlinedLabelColor,
                "& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiTouchRipple-root": {
                  color: clrPrimaryGreen,
                },
                "&:hover, &.Mui-focusVisible": {
                  color: clrPrimaryGreen,
                  backgroundColor: isDarkTheme ? "hsla(128, 52%, 47%, 0.12)" : "hsla(128, 52%, 47%, 0.08)",
                },
              },
            },
            {
              props: {variant: "outlined", color: "neutral"},
              style: {
                color: clrGrey,
                borderColor: clrGrey,
                "&:hover": {
                  backgroundColor: outlinedHoverBackground,
                  borderColor: clrGrey,
                },
              },
            },
            {
              // Turns into an outlined button on hover. The border is always present (transparent at
              // rest) and the padding is the contained padding minus the border, so the box never moves.
              props: {variant: "contained", color: "offBrandGreen"},
              style: {
                border: "2.5px solid transparent",
                padding: "3.5px 13.5px",
                "&:hover": {
                  color: offBrandDarkGreenHoverClr,
                  backgroundColor: "hsla(345, 6%, 13%, 0.05)",
                  borderColor: offBrandDarkGreenHoverClr,
                },
              },
            },
          ],
        },
      MuiIconButton: {
          variants: [
            {
              props: {color: "negative"},
              style: {
                "&:hover": {
                  backgroundColor: "hsla(335, 82%, 54%, 0.05)",
                },
              },
            },
          ],
        },
      MuiButtonBase: {
          styleOverrides: {
          root: {
            fontSize: 13.4,
            letterSpacing: 0.5,
          }

          },
        },
      MuiOutlinedInput: {
          styleOverrides: {
          root: {
            ...hideNumberArrows,
            // v4 kept the multiline padding on the root and zeroed the textarea through its own
            // `inputMultiline` slot. v5 dropped that slot, so the `input` override below now lands on
            // the textarea as well and stacks on top of the root padding. Holding the multiline padding
            // here at v4's value, and zeroing it on the textarea, keeps the field its original size.
            "&.MuiInputBase-multiline:not(.MuiInputBase-sizeSmall)": {
              padding: "18.5px 14px",
            },
          },
          input: {
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 10,
            paddingRight: 10,
            "&.MuiInputBase-inputMultiline": {
              padding: 0,
            },
            // v4 zeroed these through its own `inputAdornedStart` / `inputAdornedEnd` slots, which v5
            // also folded into `input` — without these the text sits 10px off its adornment.
            "&.MuiInputBase-inputAdornedStart": {
              paddingLeft: 0,
            },
            "&.MuiInputBase-inputAdornedEnd": {
              paddingRight: 0,
            },
            // v4's dense input (`inputMarginDense`) set only the vertical padding and left the horizontal
            // at the value above; `size="small"` is the v5 equivalent and lives on this same slot.
            "&.MuiInputBase-inputSizeSmall": {
              paddingTop: 10.5,
              paddingBottom: 10.5,
            },
            // v5 no longer forwards TextField's `margin="dense"` to the input — that is `size="small"`
            // now — so fields still using it lost v4's dense padding. Restored here rather than at the
            // ~70 call sites. Multiline is excluded so the reset above still wins for textareas.
            ".MuiFormControl-marginDense &:not(.MuiInputBase-inputMultiline)": {
              paddingTop: 10.5,
              paddingBottom: 10.5,
            },
          },
          notchedOutline: {
            // I'm glad you're seeing this ✨magic number✨, so based on the comment linked below, material ui figures out the notched outline width based on the font size.
            // https://github.com/mui/material-ui/issues/16833#issuecomment-1136374597
            // The exact number was based on trial and error. Out of all the possible things I can be doing right now and all the possible timelines I can be on, I'm here...
            fontSize: 14.70,
          }

          },
        },
      MuiInputBase: {
          styleOverrides: {
          root: {
            fontSize: 13.4,
            // v5 raised InputBase's line height from v4's 1.1876em to 1.4375em, which makes every field —
            // and every row of a multiline field — taller. Pinned back so input heights match v4.
            lineHeight: "1.1876em",
          },
          input: {
            // Same story as the line height: v5 raised the input's own height from v4's 1.1875em.
            // v5 sets `height: auto` for multiline, so that case has to be kept out of it.
            height: "1.1875em",
            "&.MuiInputBase-inputMultiline": {
              height: "auto",
            },
          }

          },
        },
      MuiFormHelperText: {
          styleOverrides: {
          contained: {
            marginLeft: 7,
            marginRight: 7,
          }

          },
        },
      MuiInput: {
          styleOverrides: {
          root: {
            ...hideNumberArrows,
          }

          },
        },
      MuiInputLabel: {
          styleOverrides: {
          outlined: {
            background: isDarkTheme ? "inherit" : colors.surface,
          }

          },
        },
      MuiBottomNavigationAction: {
          styleOverrides: {
          label: {
            fontSize: 11,
            minHeight: 32,
            // MUI leaves the label's text alignment alone, so the maxWidth below turns a label that
            // wraps ("Request Form", "Commission Info") into an 80px box of left-aligned text that
            // reads off centre under its icon.
            textAlign: "center",
            maxWidth: 80,
            "&.Mui-selected": {
              fontSize: 11.5,
            }
          }

          },
        },
      MuiTable: {
          styleOverrides: {
          root: ({theme}) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            borderCollapse: "separate"
          })

          },
        },
      MuiTooltip: {
          styleOverrides: {
          tooltip: {
            fontSize: 12,
            letterSpacing: 0.4,
            fontWeight: 400,
            color: isDarkTheme ? clrIceBlue : clrWhite,
            backgroundColor: isDarkTheme ? clrGreyBorder : "hsla(0, 0%, 38%, 0.9)",
          },
          arrow: {
            color: isDarkTheme ? clrGreyBorder : "hsla(0, 0%, 38%, 0.9)",
          }

          },
        },
      MuiTableRow: {
          styleOverrides: {
          root: {
            "&:last-child > *": {
              borderBottom: "unset",
            }
          }

          },
        },
      MuiTableCell: {
          styleOverrides: {
          root: ({theme}) => ({
            fontSize: body1,
            padding: horizontalPaddingConstant + " 0",
            borderRight: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            paddingLeft: "0.5rem",
            "&:nth-child(1)": {
              fontWeight: 500,
              paddingRight: "0.25rem"
            },
            "&:last-child": {
              borderRight: "unset"
            },
            // Same collapse as the inputs: v4 kept dense cells in their own `sizeSmall` slot, which beat
            // this root padding. v5 folds it into root, so `size="small"` tables lose their compact
            // padding unless it is restated here at v4's value.
            "&.MuiTableCell-sizeSmall": {
              padding: "6px 24px 6px 16px",
              "&:last-child": {
                paddingRight: "16px",
              },
            },
          })

          },
        },
      MuiChip: {
          styleOverrides: {
          outlined: {
            backgroundColor: isDarkTheme ? colors.surfaceSecondary : "initial",
          },
          deleteIcon: {
            color: isDarkTheme ? "lightgray" : clrGrey
          }

          },
        },
      // MUI v5 flipped these defaults to "primary"; v4 parity keeps tab labels
      // inheriting the surrounding text color instead of turning primary green.
      MuiTabs: {
          defaultProps: {
            textColor: "inherit",
            indicatorColor: "secondary",
          },
        },
      MuiTab: {
          styleOverrides: {
          root: {
            marginLeft: "auto",
            marginRight: "auto",
          }

          },
        },
      MuiSelect: {
          styleOverrides: {
          // MUI centres the caret with `top: calc(50% - .5em)`, which only lands on centre for an
          // SvgIcon whose 1em font size is its full height. The lucide carets we pass as
          // IconComponent size themselves off width/height attributes instead, so that 1em is the
          // input's 13.4px font and the icon sits low. Centre it off its own box instead.
          icon: {
            top: "50%",
            transform: "translateY(-50%)",
          },
          iconOpen: {
            transform: "translateY(-50%) rotate(180deg)",
          }

          },
        },
      MuiSvgIcon: {
          styleOverrides: {
          colorAction: {
            color: isDarkTheme ? clrIceBlue : "grey",
          }

          },
        },
    },
    typography: {
      ...MuiTypography,
      h1: {...MuiTypography.h1, fontSize: artistreeHeadingFontSizes.h1},
      h2: {...MuiTypography.h2, fontSize: artistreeHeadingFontSizes.h2},
      h3: {...MuiTypography.h3, fontSize: artistreeHeadingFontSizes.h3},
      h4: {...MuiTypography.h4, fontSize: artistreeHeadingFontSizes.h4},
      h5: {...MuiTypography.h5, fontSize: artistreeHeadingFontSizes.h5},
      h6: {...MuiTypography.h6, fontSize: artistreeHeadingFontSizes.h6},
    },
  });
}

/*
 * The styled buttons below predate the palette colours above. They are theme-independent (they also
 * render under the default MUI theme, which the shared CustomBottons tests rely on), so they stay as
 * they are; new Artistree code should use the plain <Button> with the palette colour instead.
 */

/** @deprecated use `<Button variant="contained" color="negative">` */
export const StyledNegativeButton = styled(Button)({
  color: "white",
  backgroundColor: clrRed,
  "&:hover": {
    backgroundColor: "hsl(335, 82%, 49%)",
    borderColor: clrRed,
    // Reset on touch devices, it doesn't add specificity
    "@media (hover: none)": {
      backgroundColor: clrRed
    }
  }
});

/** @deprecated use `<IconButton color="negative">` */
export const StyledNegativeIconButton = styled(IconButton)({
  color: clrRed,
  "&:hover": {
    backgroundColor: "hsla(335, 82%, 54%, 0.05)",
  }
});

/** @deprecated use `<Button variant="outlined" color="negative">` */
export const StyledNegativeOutlinedButton = styled(Button)(({theme}) => ({
  color: theme.palette.mode === DARK_THEME ? clrIceBlue : clrGrey,
  borderColor: clrRed,
  "&:hover": {
    backgroundColor: theme.palette.mode === DARK_THEME ? "hsla(335, 82%, 54%, 0.1)" : "hsla(335, 82%, 54%, 0.05)",
    borderColor: clrRed,
  }
}));

/** @deprecated use `<Button variant="contained" color="offBrandGreen">` */
export const StyledOffBrandGreenButton = styled(Button)({
  color: "white",
  backgroundColor: offBrandDarkGreenClr,
  border: "2.5px solid transparent",
  padding: "3.5px 13.5px",
  "&:hover": {
    color: offBrandDarkGreenHoverClr,
    backgroundColor: "hsla(345, 6%, 13%, 0.05)",
    borderColor: offBrandDarkGreenHoverClr,
  }
});

/** @deprecated use `<Button variant="outlined" color="green">` */
export const StyledOutlinedPrimaryGreenButton = styled(Button)(({theme}) => ({
  color: theme.palette.mode === DARK_THEME ? clrIceBlue : clrGrey,
  borderColor: clrPrimaryGreen,
  "&:hover": {
    borderColor: clrPrimaryGreen,
  },
}));

/** @deprecated use `<Button variant="outlined" color="blue">` */
export const StyledOutlinedBlueButton = styled(Button)(({theme}) => ({
  color: theme.palette.mode === DARK_THEME ? clrIceBlue : clrGrey,
  borderColor: clrSecondaryBlue,
  "&:hover": {
    borderColor: clrSecondaryBlue,
  },
}));

/** @deprecated use `<Button variant="outlined" color="neutral">` */
export const StyledOutlinedGreyButton = styled(Button)({
  color: clrGrey,
  borderColor: clrGrey,
  "&:hover": {
    borderColor: clrGrey,
  },
});

/** @deprecated use `<Button variant="contained" color="blue">` */
export const StyledBlueButton = styled(Button)({
  color: "white",
  backgroundColor: clrSecondaryBlue,
  "&:hover": {
    backgroundColor: "hsl(220, 83%, 47%)",
    // Reset on touch devices, it doesn't add specificity
    "@media (hover: none)": {
      backgroundColor: clrSecondaryBlue
    }
  }
});
